// Backend API client for the RiskaiA operations frontend.
//
// The intake wizard collects a client-side `form`; the canonical
// assessment + compliance report live in the FastAPI backend
// (riskaia-backend). This module maps the wizard form onto the
// `AssessmentInput` contract (riskaia-assessment-schema) and exposes
// the two server-backed actions the new design needs:
//
//   - createAssessment(form)  -> POST /api/assessments
//   - downloadReportPdf(id)   -> GET  /api/reports/{id}.pdf
//
// The report PDF is produced by the LaTeX EDT in riskaia-reporting.
// This frontend never generates PDFs client-side.

import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

// ---- form -> AssessmentInput mapping -------------------------------
// The wizard's Dutch public-sector vocabulary is translated into the
// backend's framework-evaluation contract. The mapping is intentionally
// conservative: sensitive domains and (semi-)automated deployment map
// onto high-risk use cases / decision_making so the rule engine reaches
// the same risk tier the client-side heuristic does.

const SECTOR_TO_INDUSTRY = {
  gemeente: "government",
  provincie: "government",
  rijk: "government",
  waterschap: "government",
  zorg: "healthcare",
  onderwijs: "education",
};

const TOEPASSING_TO_USE_CASES = {
  sociaal: ["essential_services"],
  handhaving: ["law_enforcement"],
  hr: ["hiring_screening"],
  zorg: ["healthcare_diagnostic"],
  informatie: ["search_internal"],
};

const INZET_TO_DEPLOYMENT = {
  auto: "decision_making",
  support: "decision_making",
  process: "internal",
};

const REGION_MAP = { nl: "eu", eu: "eu", world: "global" };

const DATA_TYPE_MAP = {
  personal: "personal",
  special: "health",
  biometric: "biometric",
  public: "public",
};

export function formToAssessmentInput(form) {
  const regions = (form.regions || []).map((r) => REGION_MAP[r] || r);
  const userRegions = regions.length ? [...new Set(regions)] : ["eu"];
  const hqRegion = userRegions.includes("eu")
    ? "eu"
    : userRegions.includes("global")
    ? "global"
    : "eu";

  return {
    company_name: form.orgName || "Onbekende organisatie",
    ai_system_name: form.systemName || "",
    company_size: form.size || "251-1000",
    industry: SECTOR_TO_INDUSTRY[form.sector] || "government",
    hq_region: hqRegion,
    user_regions: userRegions,
    use_cases: TOEPASSING_TO_USE_CASES[form.toepassing] || [],
    data_types: (form.dataTypes || []).map((d) => DATA_TYPE_MAP[d] || d),
    deployment: INZET_TO_DEPLOYMENT[form.inzet] || "internal",
    has_dpo: (form.safeguards || []).includes("fg"),
    has_ai_policy: (form.safeguards || []).includes("policy"),
    monitors_models: (form.safeguards || []).includes("monitoring"),
  };
}

// POST the assessment to the backend. Returns the created Assessment
// (with a server id) or null when the backend is unreachable — callers
// degrade gracefully so the client-side dossier UX keeps working.
export async function createAssessment(form) {
  try {
    const { data } = await axios.post(
      `${API}/assessments`,
      formToAssessmentInput(form)
    );
    return data;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[api] createAssessment failed:", err);
    return null;
  }
}

export async function getAssessment(id) {
  try {
    const { data } = await axios.get(`${API}/assessments/${id}`);
    return data;
  } catch {
    return null;
  }
}

function triggerPdfDownload(data, filename) {
  const url = window.URL.createObjectURL(
    new Blob([data], { type: "application/pdf" })
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// Download the LaTeX-compiled compliance report PDF for a backend
// assessment id. Streams the blob from GET /api/reports/{id}.pdf and
// triggers a browser download. Throws on failure so the caller can toast.
export async function downloadReportPdf(id, filename) {
  const { data } = await axios.get(`${API}/reports/${id}.pdf`, {
    responseType: "blob",
  });
  triggerPdfDownload(data, filename || `riskaia-rapport-${id}.pdf`);
}

// Download the official Compliance Assessment Sjabloon (report type #2).
// The HTML-form fields (organisation, system, date, per-step findings,
// signatory) are POSTed; riskaia-reporting compiles the LaTeX EDT and the
// signature is a real PDF /Sig widget (xdp-sign). Throws on failure.
export async function downloadSjabloonPdf(payload, filename) {
  const { data } = await axios.post(`${API}/reports/sjabloon.pdf`, payload, {
    responseType: "blob",
  });
  triggerPdfDownload(data, filename || "riskaia-sjabloon.pdf");
}
